import { createReference, MedplumClient } from "@medplum/core";
import { EpisodeOfCare, List } from "@medplum/fhirtypes";

import { formatISO } from "date-fns";

import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

import { PREGNANCY_RECORD_CODING_QUERY } from "../constants/record";

export const PREGNANCY_EPISODE_OF_CARE_TYPE_CODE = "episode-of-care-type/pregnancy";

/**
 * Returns whether the episode of care is typed as a pregnancy episode.
 *
 * @param episodeOfCare - FHIR `EpisodeOfCare` to inspect
 * @returns `true` when any type coding matches the pregnancy Episode of Care system and code
 */
export function isPregnancyEpisodeOfCare(episodeOfCare: EpisodeOfCare): boolean {
  return (
    episodeOfCare.type?.some((type) =>
      type.coding?.some(
        (coding) =>
          coding.system === QuiltedHealthCodeSystems.EpisodeOfCareType &&
          coding.code === PREGNANCY_EPISODE_OF_CARE_TYPE_CODE,
      ),
    ) ?? false
  );
}

/**
 * Appends an `EpisodeOfCare` entry to a pregnancy record `List` when not already linked.
 *
 * Already-linked episodes are left unchanged (date/metadata are not refreshed).
 * Episodes without an `id` are ignored so invalid List references are never written.
 *
 * @param medplum - Medplum client
 * @param pregnancyRecord - The pregnancy record `List` to update; mutated in place
 * @param episodeOfCare - FHIR `EpisodeOfCare` to link; must have a valid `id`
 * @returns The updated (or unchanged) FHIR `List` resource
 */
export async function upsertEpisodeOfCareToPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecord: List,
  episodeOfCare: EpisodeOfCare,
): Promise<List> {
  if (!episodeOfCare.id) return pregnancyRecord;

  const episodeOfCareReference = createReference(episodeOfCare);

  const alreadyLinked = pregnancyRecord.entry?.some(
    (item) => item.item?.reference === episodeOfCareReference.reference,
  );

  if (alreadyLinked) return pregnancyRecord;

  pregnancyRecord.entry = [
    ...(pregnancyRecord.entry ?? []),
    {
      item: episodeOfCareReference,
      date: episodeOfCare.period?.start ?? formatISO(new Date()),
    },
  ];

  return medplum.updateResource(pregnancyRecord);
}

/**
 * Removes an `EpisodeOfCare` entry from a pregnancy record `List` when present.
 *
 * @param medplum - Medplum client
 * @param pregnancyRecord - The pregnancy record `List` to update; mutated in place
 * @param episodeOfCare - FHIR `EpisodeOfCare` to unlink; must have a valid `id`
 * @returns The updated (or unchanged) FHIR `List` resource
 */
export async function removeEpisodeOfCareFromPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecord: List,
  episodeOfCare: EpisodeOfCare,
): Promise<List> {
  if (!episodeOfCare.id) return pregnancyRecord;

  const episodeOfCareReference = createReference(episodeOfCare).reference;
  const nextEntry = pregnancyRecord.entry?.filter(
    (item) => item.item?.reference !== episodeOfCareReference,
  );

  if ((nextEntry?.length ?? 0) === (pregnancyRecord.entry?.length ?? 0)) {
    return pregnancyRecord;
  }

  pregnancyRecord.entry = nextEntry;
  return medplum.updateResource(pregnancyRecord);
}

/**
 * Finds pregnancy record Lists whose entries include the given Episode of Care.
 *
 * @param medplum - Medplum client
 * @param patientId - Patient resource ID
 * @param episodeOfCareId - Episode of Care resource ID
 * @returns Matching pregnancy `List` resources, newest first
 */
export async function fetchPregnancyRecordListsForEpisodeOfCare(
  medplum: MedplumClient,
  patientId: string,
  episodeOfCareId: string,
): Promise<List[]> {
  const { bundle } = await medplum.searchResources("List", {
    subject: joinReferenceString("Patient", patientId),
    code: PREGNANCY_RECORD_CODING_QUERY,
    item: joinReferenceString("EpisodeOfCare", episodeOfCareId),
    _sort: "-_lastUpdated",
  });

  return getResourcesFromBundle<List>("List", bundle) ?? [];
}
